/**
 * Order Management Screen Mobile
 * 
 * Mobile version of the order management interface for managers.
 * Optimized for mobile devices with touch-friendly controls.
 * 
 * @module OrderManagementScreenMobile
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import {
  Search,
  RefreshCw,
  Eye,
  Flag as FlagIcon,
  FileText,
  FileSpreadsheet,
  Trash2
} from 'lucide-react';
import { useOrders } from '../../hooks/useOrders';
import { useAuth } from '../../contexts/AuthContext';
import { Order, OrderFilters, OrderStatus } from '../../@types/order';
import type { GlobalOrderData } from '../../hooks/useGlobalOrderData';
import { ThemeColors, Spacing, Typography } from '../../styles/ThemeStyles';
import { CSVExportService } from '../../services/CSVExportService';
import { PDFService } from '../../services/PDFService';
import { OrderManagementService } from '../../services/OrderManagementService';
import { formatOrderDateForDisplay } from '../../utils/calendarDate';
import toast, { Toaster } from 'react-hot-toast';

/**
 * Styled Components
 */
const Container = styled.div`
  min-height: 100vh;
  background: ${ThemeColors.background.default};
  padding: ${Spacing.md};
`;

const Header = styled.div`
  margin-bottom: ${Spacing.lg};
`;

const Title = styled.h1`
  font-size: ${Typography.fontSize.xxl};
  font-weight: ${Typography.fontWeight.bold};
  color: ${ThemeColors.text.primary};
  margin-bottom: ${Spacing.md};
`;

const SearchBar = styled.div`
  position: relative;
  margin-bottom: ${Spacing.md};

  svg {
    position: absolute;
    left: ${Spacing.md};
    top: 50%;
    transform: translateY(-50%);
    color: ${ThemeColors.text.secondary};
    width: 18px;
    height: 18px;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: ${Spacing.md} ${Spacing.md} ${Spacing.md} 40px;
  border: 1px solid ${ThemeColors.border.main};
  border-radius: 8px;
  font-size: ${Typography.fontSize.sm};
  background: white;

  &:focus {
    outline: none;
    border-color: ${ThemeColors.primary.main};
  }

  &::placeholder {
    color: ${ThemeColors.text.secondary};
  }
`;

const StatusTabs = styled.div`
  display: flex;
  overflow-x: auto;
  gap: ${Spacing.xs};
  margin-bottom: ${Spacing.md};
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const StatusTab = styled.button<{ active?: boolean }>`
  flex-shrink: 0;
  padding: ${Spacing.sm} ${Spacing.md};
  border: none;
  background: ${props => props.active ? ThemeColors.primary.main : 'white'};
  color: ${props => props.active ? 'white' : ThemeColors.text.primary};
  border-radius: 20px;
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.medium};
  cursor: pointer;
  text-transform: capitalize;
`;

const OrderList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.md};
`;

const OrderCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: ${Spacing.md};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const OrderHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${Spacing.sm};
`;

const OrderId = styled.div`
  font-weight: ${Typography.fontWeight.semiBold};
  color: ${ThemeColors.text.primary};
  font-size: ${Typography.fontSize.sm};
`;

const StatusBadge = styled.span<{ status: OrderStatus }>`
  display: inline-flex;
  align-items: center;
  padding: 4px ${Spacing.sm};
  border-radius: 12px;
  font-size: ${Typography.fontSize.xs};
  font-weight: ${Typography.fontWeight.medium};
  background-color: ${props => {
    switch (props.status) {
      case 'completed': return ThemeColors.success.light;
      case 'processing': return ThemeColors.info.light;
      case 'submitted': return ThemeColors.warning.light;
      case 'cancelled': return ThemeColors.error.light;
      default: return ThemeColors.gray[100];
    }
  }};
  color: ${props => {
    switch (props.status) {
      case 'completed': return ThemeColors.success.dark;
      case 'processing': return ThemeColors.info.dark;
      case 'submitted': return ThemeColors.warning.dark;
      case 'cancelled': return ThemeColors.error.dark;
      default: return ThemeColors.text.secondary;
    }
  }};
`;

const OrderInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.xs};
  margin-bottom: ${Spacing.md};
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: ${Typography.fontSize.sm};
  color: ${ThemeColors.text.secondary};
`;

const InfoLabel = styled.span`
  font-weight: ${Typography.fontWeight.medium};
`;

const InfoValue = styled.span`
  color: ${ThemeColors.text.primary};
`;

const OrderActions = styled.div`
  display: flex;
  gap: ${Spacing.xs};
  padding-top: ${Spacing.sm};
  border-top: 1px solid ${ThemeColors.border.light};
`;

const ActionButton = styled.button<{ variant?: 'success' | 'danger' | 'default' }>`
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${Spacing.xs};
  padding: ${Spacing.sm};
  border: none;
  background: ${props => {
    switch (props.variant) {
      case 'success': return ThemeColors.success.main;
      case 'danger': return ThemeColors.error.main;
      default: return ThemeColors.background.secondary;
    }
  }};
  color: ${props => props.variant ? 'white' : ThemeColors.text.primary};
  border-radius: 8px;
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.medium};
  cursor: pointer;

  svg {
    width: 16px;
    height: 16px;
  }
`;

const LoadingState = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${Spacing.xxl};
  color: ${ThemeColors.text.secondary};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${Spacing.xxl};
  color: ${ThemeColors.text.secondary};
`;

const RefreshButton = styled.button`
  position: fixed;
  bottom: ${Spacing.xl};
  right: ${Spacing.xl};
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: ${ThemeColors.primary.main};
  color: white;
  border: none;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  svg {
    width: 24px;
    height: 24px;
  }

  &:active {
    transform: scale(0.95);
  }
`;

const LoadMoreButton = styled.button`
  width: 100%;
  margin-top: ${Spacing.md};
  padding: ${Spacing.md};
  border-radius: 8px;
  border: 1px solid ${ThemeColors.border.main};
  background: white;
  color: ${ThemeColors.text.primary};
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.medium};
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

/**
 * Format currency
 */
const formatCurrency = (amount: number): string => {
  return `$${amount.toLocaleString()}`;
};

/**
 * Retrieve a user-friendly identifier for an order.
 * Prefers PO/purchase order numbers over internal IDs.
 */
const getOrderIdentifier = (order: Order): string | undefined => {
  return order.poNumber || order.purchaseOrder || order.orderId;
};

/**
 * Truncate identifiers for compact display.
 */
const formatOrderIdentifier = (identifier?: string, maxLength: number = 12): string => {
  if (!identifier) {
    return 'N/A';
  }

  return identifier.length > maxLength
    ? `${identifier.substring(0, maxLength)}...`
    : identifier;
};

/**
 * Order Management Screen Mobile Component
 */
export const OrderManagementScreenMobile: React.FC = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  
  const {
    orders,
    isLoading,
    isLoadingMore,
    fetchAllOrders,
    loadMoreOrders,
    canLoadMore,
    flagOrder,
    unflagOrder
  } = useOrders();

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<OrderStatus | 'all'>('all');

  // Debounce search input to avoid excessive queries
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  useEffect(() => {
    // Debounce search - wait 400ms after user stops typing
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 400);
    
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm]);

  /**
   * Load orders with debounced search
   */
  useEffect(() => {
    const filters: OrderFilters = {};
    
    if (selectedStatus !== 'all') {
      filters.status = selectedStatus;
    }

    if (debouncedSearchTerm) {
      filters.searchTerm = debouncedSearchTerm;
    }

    fetchAllOrders(filters, { field: 'orderDate', direction: 'desc' });
  }, [selectedStatus, debouncedSearchTerm, fetchAllOrders]);

  /**
   * Handle order view — open report in a new tab so the list stays open
   */
  const handleViewOrder = useCallback((order: Order) => {
    const path = `/manager/order-details/${encodeURIComponent(order.storeId)}/${encodeURIComponent(order.orderId)}`;
    window.open(`${window.location.origin}${path}`, '_blank', 'noopener,noreferrer');
  }, []);

  /**
   * Handle PDF download
   */
  const handleDownloadPDF = useCallback(async (order: Order, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const pdfBlob = await PDFService.generateOrderPDF({
        orderData: order as unknown as GlobalOrderData,
        orderId: order.orderId
      });
      
      const filename = `order_${order.orderId}_${new Date().toISOString().split('T')[0]}.pdf`;
      PDFService.downloadPDF(pdfBlob, filename);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF. Please try again.');
    }
  }, []);

  /**
   * Handle CSV download
   */
  const handleDownloadCSV = useCallback(async (order: Order, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await CSVExportService.exportOrderToCSV(order, {
        includeItems: true,
        includeCustomerInfo: true
      });
    } catch (error) {
      console.error('Error exporting CSV:', error);
      alert('Failed to export CSV. Please try again.');
    }
  }, []);

  /**
   * Handle refresh
   */
  const handleRefresh = useCallback(() => {
    const filters: OrderFilters = {};
    if (selectedStatus !== 'all') {
      filters.status = selectedStatus;
    }
    if (debouncedSearchTerm) {
      filters.searchTerm = debouncedSearchTerm;
    }
    fetchAllOrders(filters, { field: 'orderDate', direction: 'desc' });
  }, [selectedStatus, debouncedSearchTerm, fetchAllOrders]);

  /**
   * Handle order deletion
   */
  const handleDeleteOrder = useCallback(async (order: Order, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!user?.id) return;
    
    if (!window.confirm(`Are you sure you want to delete order ${order.orderId}? This action cannot be undone.`)) {
      return;
    }
    
    try {
      const result = await OrderManagementService.deleteOrder(
        order.storeId,
        order.orderId,
        user.id
      );
      
      if (result.success) {
        toast.success('Order deleted successfully');
        handleRefresh();
      } else {
        toast.error(result.error || 'Failed to delete order');
      }
    } catch (error: any) {
      console.error('Error deleting order:', error);
      toast.error('Failed to delete order');
    }
  }, [user, handleRefresh]);

  return (
    <Container>
      <Toaster position="top-right" />
      <Header>
        <Title>Orders</Title>
        
        <SearchBar>
          <Search />
          <SearchInput
            type="text"
            placeholder="Search by ID, store, salesperson..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </SearchBar>

        <StatusTabs>
          <StatusTab
            active={selectedStatus === 'all'}
            onClick={() => setSelectedStatus('all')}
          >
            All
          </StatusTab>
          <StatusTab
            active={selectedStatus === 'submitted'}
            onClick={() => setSelectedStatus('submitted')}
          >
            Pending
          </StatusTab>
          <StatusTab
            active={selectedStatus === 'processing'}
            onClick={() => setSelectedStatus('processing')}
          >
            Processing
          </StatusTab>
          <StatusTab
            active={selectedStatus === 'completed'}
            onClick={() => setSelectedStatus('completed')}
          >
            Completed
          </StatusTab>
        </StatusTabs>
      </Header>

      {isLoading ? (
        <LoadingState>Loading orders...</LoadingState>
      ) : orders.length === 0 ? (
        <EmptyState>No orders found</EmptyState>
      ) : (
        <>
        <OrderList>
          {orders.map((order) => {
            const rawIdentifier = getOrderIdentifier(order);

            return (
            <OrderCard key={`${order.storeId}-${order.orderId}`} onClick={() => handleViewOrder(order)}>
              <OrderHeader>
                <OrderId title={rawIdentifier || 'N/A'}>
                  {formatOrderIdentifier(rawIdentifier)}
                </OrderId>
                <StatusBadge status={order.status}>
                  {order.status}
                </StatusBadge>
              </OrderHeader>

              <OrderInfo>
                <InfoRow>
                  <InfoLabel>Store:</InfoLabel>
                  <InfoValue>{order.storeName || 'N/A'}</InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel>Date:</InfoLabel>
                  <InfoValue>{formatOrderDateForDisplay(order.orderDate, 'en-US', { month: 'short', day: 'numeric' })}</InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel>Items:</InfoLabel>
                  <InfoValue>{order.totalItems || 0}</InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel>Amount:</InfoLabel>
                  <InfoValue>{formatCurrency(order.totalAmount || 0)}</InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel>CS email:</InfoLabel>
                  <InfoValue>
                    {order.customerServiceEmailSentAt ? 'Sent' : 'Not sent'}
                  </InfoValue>
                </InfoRow>
              </OrderInfo>

              <OrderActions>
                <ActionButton onClick={(e) => { e.stopPropagation(); handleViewOrder(order); }}>
                  <Eye />
                  View
                </ActionButton>
                <ActionButton onClick={(e) => handleDownloadPDF(order, e)}>
                  <FileText />
                  PDF
                </ActionButton>
                <ActionButton onClick={(e) => handleDownloadCSV(order, e)}>
                  <FileSpreadsheet />
                  CSV
                </ActionButton>
                <ActionButton 
                  variant="danger" 
                  onClick={(e) => handleDeleteOrder(order, e)}
                >
                  <Trash2 />
                  Delete
                </ActionButton>
              </OrderActions>
            </OrderCard>
            );
          })}
        </OrderList>
        {canLoadMore && (
          <LoadMoreButton
            type="button"
            onClick={loadMoreOrders}
            disabled={isLoadingMore}
          >
            {isLoadingMore ? 'Loading…' : `Load more (${orders.length} orders shown)`}
          </LoadMoreButton>
        )}
        </>
      )}

      <RefreshButton onClick={handleRefresh}>
        <RefreshCw />
      </RefreshButton>
    </Container>
  );
};

